import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import { Plus, PencilSimple, Trash, Plant, CurrencyCircleDollar, Basket, Handshake, ChartBar, Ruler } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EMPTY_CROP, HARVEST_UNIT_OPTIONS, today, fmt } from "./constants";


export function HarvestsTab({ crops, harvests, reload }) {
  const [form, setForm] = useState({ crop_id: "", date: today(), amount: "", unit: "ton", unit_price: "", notes: "" });
  const [filterCrop, setFilterCrop] = useState("");

  // Ürün seçildiğinde son hasat fiyatı ve birimini ön-doldur
  const onCropChange = (cid) => {
    const c = crops.find(x => x.id === cid);
    setForm(f => ({
      ...f,
      crop_id: cid,
      unit: f.unit || c?.last_unit || "ton",
      unit_price: f.unit_price || c?.last_unit_price || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.crop_id) return toast.error("Ürün seçin");
    if (!(Number(form.amount) > 0)) return toast.error("Miktar 0'dan büyük olmalı");
    try {
      const r = await api.post("/fields/harvests", {
        crop_id: form.crop_id,
        date: form.date,
        amount: Number(form.amount),
        unit: form.unit,
        unit_price: Number(form.unit_price || 0),
        notes: form.notes,
      });
      toast.success(`Hasat kaydedildi: ${form.amount} ${form.unit} × ${form.unit_price || 0} ₺ = ${fmt(r.data.total_value)} ₺ değer`);
      setForm({ ...form, amount: "", notes: "" });
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const cropName = (cid) => crops.find(c => c.id === cid)?.name ?? "—";
  const filtered = filterCrop ? harvests.filter(h => h.crop_id === filterCrop) : harvests;
  const filteredVal = useMemo(() => filtered.reduce((s, x) => s + Number(x.total_value || 0), 0), [filtered]);

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card>
        <h3 className="heading text-lg mb-3">Hasat Ekle</h3>
        {crops.length === 0 ? (
          <div className="text-sm text-[#8a938c]">Önce Genel sekmesinden bir ürün ekleyin.</div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field label="Ürün *">
              <Select required data-testid="fields-harv-crop" value={form.crop_id} onChange={e => onCropChange(e.target.value)}>
                <option value="">Seçin...</option>
                {crops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Tarih *"><Input type="date" required data-testid="fields-harv-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Miktar *">
                <Input type="number" step="0.01" min="0.01" required data-testid="fields-harv-amount" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
              </Field>
              <Field label="Birim *">
                <Select data-testid="fields-harv-unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                  {HARVEST_UNIT_OPTIONS.map(u => <option key={u}>{u}</option>)}
                </Select>
              </Field>
              <Field label={`Fiyat (₺/${form.unit}) *`}>
                <Input type="number" step="0.01" min="0" required data-testid="fields-harv-price" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} />
              </Field>
            </div>
            {Number(form.amount) > 0 && Number(form.unit_price) > 0 && (
              <div className="text-xs bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36] px-3 py-2 rounded-md" data-testid="fields-harv-preview">
                ✓ {form.amount} {form.unit} × {form.unit_price} ₺ = <b>{fmt(Number(form.amount) * Number(form.unit_price))} ₺</b> üretim değeri
              </div>
            )}
            <Field label="Notlar"><Input data-testid="fields-harv-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Örn: 1. biçim, 2. biçim..." /></Field>
            <div className="text-xs text-[#5e675e] bg-[#F5F2EC] border border-[#E5E2DC] px-3 py-2 rounded">
              📦 Bu hasat doğrudan <b>Stok → "kaba yem"</b> kategorisine <b>"{crops.find(c => c.id === form.crop_id)?.name || "ürün adı"}"</b> olarak eklenir.<br/>
              <span className="text-[#8a938c]">Silmek/düzeltmek için Stok modülünü kullanmanız önerilir (tek doğruluk kaynağı).</span>
            </div>
            <Button type="submit" data-testid="fields-harv-submit"><Plus size={14} className="inline mr-1" /> Kaydet</Button>
          </form>
        )}
      </Card>

      <Card className="lg:col-span-2 !p-0 overflow-hidden">
        <div className="p-4 pb-3 border-b border-[#E5E2DC] flex flex-wrap items-center gap-2 justify-between">
          <h3 className="heading text-lg">Hasat Geçmişi</h3>
          <div className="flex items-center gap-3">
            <Select data-testid="fields-harv-filter" value={filterCrop} onChange={e => setFilterCrop(e.target.value)} className="!py-1 text-xs">
              <option value="">Tümü</option>
              {crops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <span className="text-xs text-[#8a938c]">
              Toplam Değer: <b className="text-[#2D4A36]">{fmt(filteredVal)} ₺</b>
            </span>
          </div>
        </div>
        <table className="data-table">
          <thead><tr><th>Tarih</th><th>Ürün</th><th className="text-right">Miktar</th><th className="text-right">Fiyat</th><th className="text-right">Değer</th><th>Notlar</th></tr></thead>
          <tbody>
            {filtered.slice(0, 200).map(x => (
              <tr key={x.id} data-testid={`fields-harv-row-${x.id}`}>
                <td className="tabnum">{x.date}</td>
                <td>{cropName(x.crop_id)}</td>
                <td className="text-right tabnum font-semibold">{fmt(x.amount)} {x.unit || "birim"}</td>
                <td className="text-right tabnum">{fmt(x.unit_price)} ₺</td>
                <td className="text-right tabnum font-semibold text-[#2D4A36]">{fmt(x.total_value)} ₺</td>
                <td className="text-xs text-[#6b746c]">{x.notes || "—"}</td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={6} className="text-center py-8 text-[#8a938c]">Hasat kaydı yok</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ---------------- SALES: Mahsul Satışı — otomatik Finans → Gelirler ----------------
